import { useState, useEffect } from 'react';
import { api } from '../api/client';

const GATEWAY_URL = import.meta.env.VITE_API_BASE ?? window.location.origin;

export function ApiKeysPage() {
  const [keys, setKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [revealed, setRevealed] = useState<any>(null);
  const [copied, setCopied] = useState(false);

  const load = () => api.listApiKeys().then(d => { setKeys(d.api_keys ?? []); setLoading(false); });
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const result = await api.createApiKey(newKeyName.trim());
      setRevealed(result);
      setNewKeyName('');
      load();
    } catch (e: any) {
      alert(e.message || String(e));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: string, prefix: string) => {
    if (!confirm(`Revoke key ${prefix}...? This cannot be undone.`)) return;
    await api.revokeApiKey(id);
    load();
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">API Keys</h1>
          <p className="page-subtitle">Authenticate OpenAI-compatible clients to Erix Life</p>
        </div>
      </div>

      {/* Connection info */}
      <div className="card card--accent" style={{ marginBottom: '24px' }}>
        <div className="card__title">🔌 Client Setup</div>
        <div className="grid-2">
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Base URL</div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <code style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: '#a78bfa', background: 'rgba(124,58,237,0.1)', padding: '6px 10px', borderRadius: '6px', flex: 1 }}>
                {GATEWAY_URL}/v1
              </code>
              <button className="btn btn--secondary btn--sm" onClick={() => copy(`${GATEWAY_URL}/v1`)}>Copy</button>
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Auth header</div>
            <code style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: '#22d3ee', background: 'rgba(6,182,212,0.1)', padding: '6px 10px', borderRadius: '6px', display: 'block' }}>
              Authorization: Bearer sk-eri-...
            </code>
          </div>
        </div>

        <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
          <div><strong style={{ color: '#e2e8f0' }}>n8n & API Integrations:</strong> Use model <code style={{ fontFamily: 'var(--font-mono)', color: '#a78bfa' }}>erix-life</code> (Pure Sandbox, hides reasoning, clean stream).</div>
        </div>
      </div>

      {/* Revealed key modal */}
      {revealed && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ maxWidth: '480px', width: '100%', border: '1px solid var(--border-accent)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{ fontSize: '20px' }}>🔑</span>
              <div style={{ fontWeight: 600 }}>API Key Created: {revealed.name}</div>
            </div>
            <div className="api-key-reveal">{revealed.key}</div>
            <div className="key-warning">⚠️ Save this key now — it will never be shown again.</div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button className="btn btn--primary" onClick={() => copy(revealed.key)} style={{ flex: 1, justifyContent: 'center' }}>
                {copied ? '✓ Copied!' : 'Copy Key'}
              </button>
              <button className="btn btn--secondary" onClick={() => setRevealed(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Create new key */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card__title">Create New API Key</div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            className="input"
            placeholder="Key name (e.g. Cursor Dev, VS Code)"
            value={newKeyName}
            onChange={e => setNewKeyName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && create()}
            style={{ flex: 1 }}
          />
          <button className="btn btn--primary" onClick={create} disabled={creating || !newKeyName.trim()}>
            {creating ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '+ Generate'}
          </button>
        </div>
      </div>

      {/* Keys table */}
      <div className="card">
        <div className="card__title">Your API Keys</div>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '30px' }}><div className="spinner" /></div>
        ) : !keys.length ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '13px' }}>
            No API keys yet. Create one above.
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Last Used</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {keys.map(k => (
                  <tr key={k.id}>
                    <td style={{ fontWeight: 500 }}>{k.name}</td>
                    <td>
                      <code style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: '#a78bfa' }}>
                        {k.key_prefix}••••••••
                      </code>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                      {k.last_used ? new Date(k.last_used).toLocaleString() : 'Never'}
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                      {new Date(k.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <button className="btn btn--danger btn--sm" onClick={() => revoke(k.id, k.key_prefix)}>
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
